import React, { useState, useEffect } from 'react'
import { Grid } from '@material-ui/core'
import classnames from 'classnames'
import { useDispatch } from 'react-redux'

import { DateTimePicker } from '../../../Form'

import { showReduxModal, hideReduxModal } from '../../../../Reducers/UI/actions'
import SubmitButton from '../../../Form/SubmitButton'
import FormButton from '../../../Form/Button'
import { SectionLabel } from '../../../Section'
import { PlaceAutocomplete } from './PlaceAutocomplete'
import pinIcon from 'Assets/Images/icons/mission-pin-vector.svg'
import { Map } from 'Components/Map'
import {
  formatPolygons,
  getMinDate,
  getDefaultDate,
  getMeetingPointStatus,
  getDefaultCenter,
  getHint,
  getTitle,
  getMaxDate,
  handleDateTimeError,
  formatAddress,
} from './utils'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalTitle } from 'Components/Modal/ModalTitle'
import { ModalBody } from 'Components/Modal/ModalBody'
import useStyles from './styles'
import { updateMissionSimplified } from 'Module/graphQL'
import { MEETING_POINT_STATUS } from './options'
import Dialog from '@material-ui/core/Dialog'

const SetMeetingPointModal = ({
  city,
  countryCode,
  missionsToEdit,
  onClose,
  onRefetchMissions,
  isOpen,
  // Server-side mode (letterbox city plans): one call for the whole selection.
  onSubmitBulk = null,
}) => {
  if (!missionsToEdit.length) return null

  const status = getMeetingPointStatus(missionsToEdit)
  const defaultDate = getDefaultDate(missionsToEdit, status)
  const minDate = getMinDate(missionsToEdit)
  const maxDate = getMaxDate(missionsToEdit)
  const defaultCenter = getDefaultCenter(missionsToEdit, status)

  const [isLoading, setIsLoading] = useState(false)
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false)
  const [location, setLocation] = useState(null)
  const [defaultCenterAddress, setDefaultCenterAddress] = useState('')
  const [dateTime, setDateTime] = useState(defaultDate)
  const [isExpanded, setIsExpanded] = useState(false)
  const [address, setAddress] = useState('')
  const [dateError, setDateError] = useState(null)
  // const [isAddressError, setIsAddressError] = useState(null)
  const [mapCenter, setMapCenter] = useState(defaultCenter)

  const dispatch = useDispatch()
  const classes = useStyles()

  useEffect(() => {
    if (
      [
        MEETING_POINT_STATUS.MULTIPLE_SET,
        MEETING_POINT_STATUS.MULTIPLE_ADD,
        MEETING_POINT_STATUS.SINGLE_ADD,
      ].includes(status)
    ) {
      if (!defaultCenter) return

      if (!window.google || !window.google.maps) return
      const geocoder = new window.google.maps.Geocoder()

      geocoder.geocode({ location: defaultCenter }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const formattedAddress = formatAddress(results[0].address_components)
          // if (formattedAddress === 'Enter address manually') {
          //   setIsAddressError('Address not found, please put it manually')
          // } else {
          //   setIsAddressError(null)
          // }
          setDefaultCenterAddress(formattedAddress)
        } else {
          setDefaultCenterAddress('')
        }
      })
    } else if (
      [
        MEETING_POINT_STATUS.SINGLE_EDIT,
        MEETING_POINT_STATUS.MULTIPLE_EDIT,
      ].includes(status)
    ) {
      const address = missionsToEdit[0].meetingPoint.address
      setAddress(address)
      setDefaultCenterAddress(address)
    }
  }, [JSON.stringify(defaultCenter)])

  useEffect(() => {
    if (
      !dateTime ||
      !address ||
      !(location
        ? location.lng && location.lat
        : defaultCenterAddress && defaultCenter) ||
      dateError ||
      address === 'Enter address manually'
    ) {
      setSubmitButtonDisabled(true)
    } else {
      setSubmitButtonDisabled(false)
    }
  }, [dateTime, location, defaultCenterAddress, defaultCenter, address])

  useEffect(() => {
    if (dateError) {
      dispatch(
        showReduxModal({
          title: 'Warning!',
          children: dateError,
          onConfirm: hideReduxModal,
          hideCancel: true,
        }),
      )
    }

    // if (isAddressError) {
    //   dispatch(
    //     showReduxModal({
    //       title: 'Warning!',
    //       children: isAddressError,
    //       onConfirm: hideReduxModal,
    //       hideCancel: true,
    //     }),
    //   )
    // }
  }, [dateError])

  const handlePlaceChanged = place => {
    const lat =
      typeof place.geometry.location.lat === 'function'
        ? place.geometry.location.lat()
        : place.geometry.location.lat
    const lng =
      typeof place.geometry.location.lng === 'function'
        ? place.geometry.location.lng()
        : place.geometry.location.lng

    const newLocation = {
      lat,
      lng,
    }

    setLocation(newLocation)
    setMapCenter(newLocation)
    setAddress(place.formattedAddress)
  }

  const handleMapMarkerDrag = (lat, lng) => {
    if (!window.google || !window.google.maps) return
    const geocoder = new window.google.maps.Geocoder()
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const newLocation = {
          lat,
          lng,
        }

        const formattedAddress = formatAddress(results[0].address_components)
        // if (formattedAddress === 'Enter address manually') {
        //   setIsAddressError('Address not found, please put it manually')
        // } else {
        //   setIsAddressError(null)
        // }

        setLocation(newLocation)
        setMapCenter(newLocation)
        setAddress(formattedAddress)
      } else {
        setLocation({ lat, lng })
        setAddress('')
      }
    })
  }

  const handleClose = () => {
    onClose()
  }

  const onDateChange = dateTime => {
    handleDateTimeError({ dateTime, minDate, maxDate, status, setDateError })

    setDateTime(dateTime)
  }

  const handleSubmit = async () => {
    setIsLoading(true)

    try {
      const coordinates = location
        ? { latitude: location.lat, longitude: location.lng }
        : { latitude: defaultCenter.lat, longitude: defaultCenter.lng }

      const meetingPoint = {
        ...coordinates,
        address: address || defaultCenterAddress,
        dateTime,
      }

      if (onSubmitBulk) {
        await onSubmitBulk(meetingPoint)
      } else {
        for (const mission of missionsToEdit) {
          const missionDataInput = {
            missionInput: {
              id: mission.id,
              meetingPoint,
            },
          }

          await updateMissionSimplified(missionDataInput)
        }
      }

      handleClose()
      await onRefetchMissions()
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const hint = getHint(status, classes)
  const title = getTitle(status)

  return (
    <Dialog
      open={isOpen}
      disableEnforceFocus={true}
      classes={{ paper: classes.modal, paperWidthSm: classes.paperWidthSm }}
    >
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Grid container spacing={5}>
          <Grid item xs={12} className={classes.gridPadding}>
            <SectionLabel>Setting meeting point for</SectionLabel>
            <div>
              <div
                className={classnames(
                  classes.expandableList,
                  isExpanded
                    ? classes.expandableListExpanded
                    : classes.expandableListCollapsed,
                )}
              >
                {missionsToEdit.map((mission, idx) => (
                  <span key={mission.id}>
                    {mission.label}
                    {idx !== missionsToEdit.length - 1 && ', '}
                  </span>
                ))}
              </div>
              {missionsToEdit.length > 1 ? (
                <div
                  className={classes.viewMoreBtn}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <span className={classes.viewMoreText}>
                    {isExpanded ? 'View less' : 'View more'}
                  </span>
                  {isExpanded ? (
                    <ExpandLessIcon className={classes.expandIcon} />
                  ) : (
                    <ExpandMoreIcon className={classes.expandIcon} />
                  )}
                </div>
              ) : null}
            </div>
          </Grid>
          {hint ? (
            <Grid item xs={12} className={classes.gridPadding}>
              {hint}
            </Grid>
          ) : null}
          <Grid item xs={12} className={classes.gridPadding}>
            <SectionLabel>Date</SectionLabel>
            <DateTimePicker
              minDate={minDate}
              maxDate={maxDate}
              input={{
                value: dateTime,
                onChange: onDateChange,
              }}
            />
          </Grid>
          <Grid item xs={12} className={classes.gridPadding}>
            <SectionLabel>Address</SectionLabel>
            {defaultCenterAddress ? (
              <PlaceAutocomplete
                countryCode={countryCode}
                placeholder={'Enter a city, neighborhood, or specific address'}
                onPlaceChanged={handlePlaceChanged}
                defaultValue={defaultCenterAddress}
                city={city}
                inputValue={address}
                setInputValue={setAddress}
                // setIsAddressError={setIsAddressError}
              />
            ) : (
              <div>Loading...</div>
            )}
          </Grid>
          <Grid item xs={12} className={classes.gridPaddingMap}>
            <Map
              defaultZoom={12}
              mapPositionLat={mapCenter.lat}
              mapPositionLng={mapCenter.lng}
              useDefaultCenter={false}
              markers={[
                {
                  markerPositionLat: location
                    ? location.lat
                    : defaultCenter.lat,
                  markerPositionLng: location
                    ? location.lng
                    : defaultCenter.lng,
                  onMarkerDragEnd: e => {
                    const lat = e.latLng.lat()
                    const lng = e.latLng.lng()
                    handleMapMarkerDrag(lat, lng)
                  },
                  isMarkerDraggable: true,
                  useDefaultIcon: true,
                  icon: {
                    url: pinIcon,
                    scaledSize: { width: 40, height: 40 },
                  },
                },
              ]}
              polygon={formatPolygons(missionsToEdit)}
            />
          </Grid>
          <div className={classes.buttonsContainer}>
            <FormButton
              disabled={isLoading}
              value="Cancel"
              onClick={handleClose}
            />
            <SubmitButton
              onClick={handleSubmit}
              disabled={submitButtonDisabled || isLoading}
              value={isLoading ? 'Loading...' : 'CONFIRM'}
            />
          </div>
        </Grid>
      </ModalBody>
    </Dialog>
  )
}

export default SetMeetingPointModal
